/**
 * PC 全业务链路上下文：统一 farmId / landId / deviceId 的 query 读写与跳转拼接。
 * 关键：跨页跳转、刷新、返回时尽量保留上下游同一口径。
 */
export const BUSINESS_CONTEXT_KEYS = ['farmId', 'landId', 'deviceId'];

/** 标准空态文案（业务无数据） */
export const EMPTY_COPY = Object.freeze({
  NOT_CONNECTED: '暂未接入',
  NO_RECORD: '暂无记录',
});

/** 三类异常场景短反馈（断网 / 无权限 / 加载失败） */
export const SCENE_COPY = Object.freeze({
  OFFLINE: '网络已断开，请检查连接后重试',
  NO_ACCESS: '暂无访问权限',
  LOAD_ERROR: '加载失败，请重试',
});

function readOne(searchParams, key) {
  if (!searchParams) return '';
  if (typeof searchParams.get === 'function') return String(searchParams.get(key) || '').trim();
  return String(searchParams[key] || '').trim();
}

/** 从 location.search / URLSearchParams / 普通对象读取业务上下文 */
export function readBusinessContext(searchParams) {
  return {
    farmId: readOne(searchParams, 'farmId'),
    // 统计等页历史用 landParcelId，与 landId 同义口径
    landId: readOne(searchParams, 'landId') || readOne(searchParams, 'landParcelId'),
    deviceId: readOne(searchParams, 'deviceId'),
  };
}

/**
 * 在现有 search 上合并业务上下文键（只动 farmId/landId/deviceId）。
 * @param {string|URLSearchParams} currentSearch
 * @param {Record<string, string|number|null|undefined>} patch
 */
export function mergeBusinessContextSearch(currentSearch, patch = {}) {
  const raw =
    typeof currentSearch === 'string'
      ? currentSearch.replace(/^\?/, '')
      : currentSearch instanceof URLSearchParams
        ? currentSearch.toString()
        : '';
  const params = new URLSearchParams(raw);
  Object.entries(patch || {}).forEach(([key, value]) => {
    if (!BUSINESS_CONTEXT_KEYS.includes(key)) return;
    if (value == null || String(value).trim() === '') params.delete(key);
    else params.set(key, String(value).trim());
  });
  return params.toString();
}

/** 只抽取业务上下文三键，避免杂参跨页污染 */
export function pickBusinessContextSearch(search) {
  const raw =
    typeof search === 'string'
      ? search.replace(/^\?/, '')
      : search instanceof URLSearchParams
        ? search.toString()
        : '';
  const params = new URLSearchParams(raw);
  return mergeBusinessContextSearch('', readBusinessContext(params));
}

/**
 * path + 业务上下文 search。
 * 若 path 自带 query（如 tab=entries），保留非业务键，再叠 farmId/landId/deviceId。
 * @param {string} path
 * @param {string|URLSearchParams|Record<string, string|number|null|undefined>} search
 */
export function withBusinessContextSearch(path, search) {
  const rawPath = String(path || '');
  const qIdx = rawPath.indexOf('?');
  const base = qIdx >= 0 ? rawPath.slice(0, qIdx) : rawPath;
  const pathExisting = qIdx >= 0 ? rawPath.slice(qIdx + 1) : '';

  let businessQ = '';
  if (typeof search === 'string') {
    businessQ = pickBusinessContextSearch(search);
  } else if (search instanceof URLSearchParams) {
    businessQ = pickBusinessContextSearch(search);
  } else if (search && typeof search === 'object') {
    businessQ = mergeBusinessContextSearch('', search);
  }

  const params = new URLSearchParams(pathExisting);
  BUSINESS_CONTEXT_KEYS.forEach((key) => params.delete(key));
  new URLSearchParams(businessQ).forEach((value, key) => {
    params.set(key, value);
  });
  const q = params.toString();
  return q ? `${base}?${q}` : base;
}

/**
 * 构建带 farmId/landId/deviceId 的跳转路径（可叠当前 search 再 patch）。
 * patch 中空字符串/null 表示「不覆盖」，保留 currentSearch 已有值（避免误删 deviceId）。
 * 若需显式删除某键，请用 mergeBusinessContextSearch 对 search 操作。
 * @param {string} path
 * @param {Record<string, string|number|null|undefined>} ctx
 * @param {string|URLSearchParams} [currentSearch]
 */
export function buildBusinessContextPath(path, ctx = {}, currentSearch = '') {
  const patch = {};
  Object.entries(ctx || {}).forEach(([key, value]) => {
    if (!BUSINESS_CONTEXT_KEYS.includes(key)) return;
    if (value == null || String(value).trim() === '') return;
    patch[key] = value;
  });
  const next = mergeBusinessContextSearch(pickBusinessContextSearch(currentSearch), patch);
  return withBusinessContextSearch(path, next);
}

/**
 * 从当前 location 透传到目标 path（仅三键）。
 * @param {string} path
 * @param {{ search?: string, pathname?: string }|string} locationOrSearch
 */
export function preserveBusinessContextTo(path, locationOrSearch) {
  const search =
    typeof locationOrSearch === 'string'
      ? locationOrSearch
      : locationOrSearch?.search || '';
  return withBusinessContextSearch(path, search);
}
